import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  listMyTickets,
  createTicket,
  type SupportTicket,
} from "@/lib/support.functions";

const STATUSES = ["all", "open", "in_progress", "resolved", "closed"] as const;
type StatusFilter = (typeof STATUSES)[number];

const CATEGORY_OPTIONS = [
  { value: "bug", label: "Bug" },
  { value: "question", label: "Question" },
  { value: "billing", label: "Billing" },
  { value: "feature", label: "Feature request" },
  { value: "other", label: "Other" },
] as const;

const searchSchema = z.object({
  status: z.enum(STATUSES).optional(),
});

export const Route = createFileRoute("/_authed/support")({
  head: () => ({
    meta: [
      { title: "Support: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: searchSchema,
  component: SupportPage,
});

function statusBadgeClass(s: SupportTicket["status"]): string {
  switch (s) {
    case "open":
      return "bg-amber-100 text-amber-900 border-amber-200";
    case "in_progress":
      return "bg-blue-100 text-blue-900 border-blue-200";
    case "resolved":
      return "bg-emerald-100 text-emerald-900 border-emerald-200";
    case "closed":
      return "bg-zinc-100 text-zinc-700 border-zinc-200";
  }
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function SupportPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const listFn = useServerFn(listMyTickets);
  const createFn = useServerFn(createTicket);

  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [showForm, setShowForm] = React.useState(false);
  const [subject, setSubject] = React.useState("");
  const [category, setCategory] = React.useState<string>("other");
  const [message, setMessage] = React.useState("");

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["my-support-tickets", statusFilter],
    queryFn: () =>
      listFn({
        data: { status: statusFilter === "all" ? null : statusFilter },
      }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          subject: subject.trim(),
          category: category as never,
          message: message.trim(),
        },
      }),
    onSuccess: (res) => {
      toast.success("Ticket opened");
      setSubject("");
      setMessage("");
      setCategory("other");
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ["my-support-tickets"] });
      navigate({ to: "/support/$ticketId", params: { ticketId: res.ticketId } });
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Failed to open ticket");
    },
  });

  const canSubmit =
    subject.trim().length > 0 &&
    message.trim().length > 0 &&
    !createMut.isPending;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl text-brand-ink">Support</h1>
          <p className="text-sm text-brand-muted">
            Open a ticket and we'll reply by email, typically within one business day.
          </p>
        </div>
        <Button onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "New ticket"}
        </Button>
      </div>

      {showForm ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) createMut.mutate();
          }}
          className="mb-8 space-y-4 rounded-lg border border-brand-line bg-brand-paper p-5"
        >
          <div className="space-y-2">
            <label className="block text-xs font-medium uppercase tracking-wider text-brand-muted">
              Subject
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              placeholder="Brief summary"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium uppercase tracking-wider text-brand-muted">
              Category
            </label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium uppercase tracking-wider text-brand-muted">
              Message
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={10000}
              required
              placeholder="Tell us what's going on. Include links or screenshots if helpful."
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={!canSubmit}>
              {createMut.isPending ? "Opening…" : "Open ticket"}
            </Button>
          </div>
        </form>
      ) : null}

      <div className="mb-3 flex items-center gap-3">
        <label className="text-xs font-medium uppercase tracking-wider text-brand-muted">
          Status
        </label>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-brand-line">
        <table className="w-full text-sm">
          <thead className="bg-brand-paper-2 text-left text-xs uppercase tracking-wider text-brand-muted">
            <tr>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-brand-muted">
                  Loading…
                </td>
              </tr>
            ) : !tickets || tickets.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-brand-muted">
                  No support tickets yet. Open one above when you need help.
                </td>
              </tr>
            ) : (
              tickets.map((t) => (
                <tr
                  key={t.id}
                  className="border-t border-brand-line hover:bg-brand-paper-2"
                >
                  <td>
                    <Link to="/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 text-brand-muted no-underline">
                      {fmtDate(t.created_at)}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to="/support/$ticketId"
                      params={{ ticketId: t.id }}
                      className="block cursor-pointer px-3 py-2 text-brand-ink underline decoration-brand-line underline-offset-4 hover:decoration-brand-ink"
                    >
                      {t.subject}
                    </Link>
                  </td>
                  <td>
                    <Link to="/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 capitalize text-brand-ink/80 no-underline">
                      {t.category ?? "—"}
                    </Link>
                  </td>
                  <td>
                    <Link to="/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 no-underline">
                      <Badge variant="outline" className={statusBadgeClass(t.status)}>
                        {t.status.replace("_", " ")}
                      </Badge>
                    </Link>
                  </td>
                  <td>
                    <Link to="/support/$ticketId" params={{ ticketId: t.id }} className="block cursor-pointer px-3 py-2 text-brand-muted no-underline">
                      {fmtDate(t.last_message_at)}
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}